import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  addDays, fmtFull, fmtShort, iso, parseIso, rangeDays,
  startOfMonth, endOfMonth, MOIS, JOURS_LONG,
} from "@/lib/date";
import { ChevronLeft, ChevronRight, Save, AlertTriangle, CalendarRange, Check, X, Ban, Lock, BellRing, LayoutGrid, LayoutList, CalendarDays } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const VIEW_KEY = "pecan_dispos_view";

// Bouton pour le toggle vue Liste / Semaine / Mois
function ViewToggleBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
        active ? "bg-white text-[#2C4C3B] shadow" : "text-[#6b7f79] hover:text-[#2C4C3B]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

// Cellule bulle réutilisée par les vues Semaine et Mois
function DispoCell({ dayDate, item, closed, closureName, closureCustom, disabled, onClick, onEdit, testId }) {
  const day = dayDate.getDate();
  const dow = JOURS_LONG[(dayDate.getDay() + 6) % 7].slice(0, 3);

  let bg = "bg-white border border-[#DDE7E4] text-[#6b7f79]";
  let content = "?";
  let sub = null;
  if (closed) {
    bg = closureCustom ? "bg-[#DDECE8] text-[#3C6750] border border-[#C9DBD6]" : "bg-[#F1EDE3] text-[#94ADA7] border border-[#E4EDEA]";
    content = closureName || (dayDate.getDay() === 0 ? "Fermé" : "Férié");
  } else if (item?.available) {
    bg = "bg-[#1B5E20] text-white border border-[#155018] shadow-sm";
    content = "Dispo";
    if (item.start && item.end) {
      sub = `${(item.start || "").slice(0, 5)}–${(item.end || "").slice(0, 5)}`;
    }
  } else if (item && item.available === false) {
    bg = "bg-[#C62828] text-white border border-[#A02020] shadow-sm";
    content = "Non";
  }

  return (
    <div className="flex flex-col items-center gap-1 min-w-0" data-testid={`${testId}-wrap`}>
      <div className="text-[10px] uppercase tracking-wider text-[#94ADA7] font-semibold leading-none">{dow}</div>
      <div className="font-display text-lg text-[#1A2B27] leading-none">{day}</div>
      <button
        type="button"
        disabled={disabled || closed}
        onClick={onClick}
        data-testid={testId}
        title={closed ? closureName || "Fermé" : (item?.available ? "Clique pour marquer indisponible" : "Clique pour marquer dispo")}
        className={`w-full min-h-[52px] rounded-2xl px-2 py-1.5 text-xs font-semibold flex flex-col items-center justify-center ${bg} ${disabled || closed ? "cursor-default" : "cursor-pointer hover:scale-[1.02] active:scale-95 transition-transform"}`}
      >
        <span>{content}</span>
        {sub && <span className="text-[10px] opacity-90 font-normal mt-0.5">{sub}</span>}
      </button>
      {!closed && item?.available && !disabled && (
        <button
          type="button"
          onClick={onEdit}
          data-testid={`${testId}-edit`}
          className="text-[9px] text-[#2C4C3B] hover:underline font-semibold"
        >
          ⚙ ajuster
        </button>
      )}
    </div>
  );
}

// Grille semaine 7 jours (avec pagination)
function DisposWeekGrid({ days, items, holidays, weekOffset, setWeekOffset, onToggle, onEditCell, disabled }) {
  // Chunker en semaines calendaires (lundi -> dimanche)
  const weeks = [];
  let current = [];
  days.forEach((d) => {
    if (current.length === 0 || d.getDay() === 1) {
      if (current.length) weeks.push(current);
      current = [d];
    } else {
      current.push(d);
    }
  });
  if (current.length) weeks.push(current);

  const totalWeeks = weeks.length;
  const idx = Math.min(Math.max(weekOffset, 0), Math.max(0, totalWeeks - 1));
  const currentWeek = weeks[idx] || [];

  const label = currentWeek.length > 0
    ? `${fmtShort(currentWeek[0])} → ${fmtShort(currentWeek[currentWeek.length - 1])}`
    : "";

  const toggleFor = (d) => {
    const isoD = iso(d);
    const v = items[isoD];
    // Cycle : undefined -> available:true -> available:false -> available:true
    if (!v || v.available === undefined) onToggle(isoD, true);
    else onToggle(isoD, !v.available);
  };

  return (
    <Card className="card-soft border-0" data-testid="dispos-week-grid">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" size="icon" onClick={() => setWeekOffset(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="dispos-week-prev" className="rounded-full hover:bg-[#F1EDE3]">
            <ChevronLeft className="w-5 h-5 text-[#2C4C3B]" />
          </Button>
          <span className="text-sm font-semibold text-[#1A2B27]">{label || "—"}</span>
          <Button variant="ghost" size="icon" onClick={() => setWeekOffset(Math.min(totalWeeks - 1, idx + 1))} disabled={idx >= totalWeeks - 1} data-testid="dispos-week-next" className="rounded-full hover:bg-[#F1EDE3]">
            <ChevronRight className="w-5 h-5 text-[#2C4C3B]" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-2">
          {currentWeek.map((d) => {
            const isoD = iso(d);
            const hol = holidays[isoD];
            const closed = d.getDay() === 0 || !!hol;
            return (
              <DispoCell
                key={isoD}
                dayDate={d}
                item={items[isoD]}
                closed={closed}
                closureName={hol?.name || (d.getDay() === 0 ? "Dimanche" : "Férié")}
                closureCustom={hol?.source === "custom"}
                disabled={disabled}
                onClick={() => toggleFor(d)}
                onEdit={() => onEditCell(isoD)}
                testId={`dispo-week-cell-${isoD}`}
              />
            );
          })}
        </div>
        <DispoLegend />
      </CardContent>
    </Card>
  );
}

// Grille mois — calendrier complet 5-6 lignes
function DisposMonthGrid({ days, items, holidays, onToggle, onEditCell, disabled }) {
  // La période peut ne pas être alignée sur un mois calendaire — on pad début / fin sur les semaines.
  if (days.length === 0) return null;
  const first = days[0];
  const last = days[days.length - 1];
  const padStart = (first.getDay() + 6) % 7; // 0=lundi
  const padEnd = 6 - ((last.getDay() + 6) % 7);
  const cells = [];
  for (let i = padStart; i > 0; i--) cells.push({ d: new Date(first.getFullYear(), first.getMonth(), first.getDate() - i), out: true });
  days.forEach((d) => cells.push({ d, out: false }));
  for (let i = 1; i <= padEnd; i++) cells.push({ d: new Date(last.getFullYear(), last.getMonth(), last.getDate() + i), out: true });

  const toggleFor = (d) => {
    const isoD = iso(d);
    const v = items[isoD];
    if (!v || v.available === undefined) onToggle(isoD, true);
    else onToggle(isoD, !v.available);
  };

  return (
    <Card className="card-soft border-0 overflow-hidden" data-testid="dispos-month-grid">
      <CardContent className="p-0">
        <div className="grid grid-cols-7 border-b border-[#DDE7E4] bg-[#F1EDE3]">
          {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((j) => (
            <div key={j} className="p-2 text-[10px] font-semibold uppercase tracking-wider text-[#2C4C3B] text-center">
              {j}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map(({ d, out }, i) => {
            const isoD = iso(d);
            const hol = holidays[isoD];
            const closed = d.getDay() === 0 || !!hol;
            const v = items[isoD];

            let bg = "bg-white";
            let content = null;
            let sub = null;
            if (out) {
              bg = "bg-[#FAFCFB]";
            } else if (closed) {
              bg = hol?.source === "custom" ? "bg-[#DDECE8]" : "bg-[#F1EDE3]";
            } else if (v?.available) {
              bg = "bg-[#1B5E20] text-white";
              content = "Dispo";
              if (v.start && v.end) sub = `${(v.start || "").slice(0, 5)}–${(v.end || "").slice(0, 5)}`;
            } else if (v && v.available === false) {
              bg = "bg-[#C62828] text-white";
              content = "Non";
            }
            const clickable = !out && !closed && !disabled;
            return (
              <button
                type="button"
                key={`${isoD}-${i}`}
                onClick={() => clickable && toggleFor(d)}
                disabled={!clickable}
                data-testid={out ? undefined : `dispo-month-cell-${isoD}`}
                className={`border-b border-r border-[#DDE7E4] p-2 min-h-[72px] text-left flex flex-col ${bg} ${clickable ? "hover:brightness-95 active:scale-[0.98] transition-all cursor-pointer" : "cursor-default"} ${out ? "opacity-40" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-semibold ${(v?.available || (v && v.available === false)) ? "" : "text-[#1A2B27]"}`}>
                    {d.getDate()}
                  </span>
                  {closed && !out && (
                    <span className="text-[9px] uppercase opacity-70">
                      {d.getDay() === 0 ? "Dim" : "Férié"}
                    </span>
                  )}
                </div>
                {content && (
                  <div className="mt-auto text-[11px] font-semibold">
                    {content}
                    {sub && <div className="text-[9px] opacity-90 font-normal">{sub}</div>}
                  </div>
                )}
                {!out && !closed && v?.available && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); onEditCell(isoD); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onEditCell(isoD); } }}
                    data-testid={`dispo-month-cell-${isoD}-edit`}
                    className="mt-1 text-[9px] underline opacity-90 self-start"
                  >
                    ⚙ ajuster
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="p-3 border-t border-[#DDE7E4]">
          <DispoLegend />
        </div>
      </CardContent>
    </Card>
  );
}

function DispoLegend() {
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-[#6b7f79]">
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#1B5E20]"></span> Dispo</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#C62828]"></span> Non dispo</span>
      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-[#F1EDE3] border border-[#E4EDEA]"></span> Fermé</span>
      <span className="ml-auto italic">Clique une case pour basculer · ⚙ pour ajuster horaires/note</span>
    </div>
  );
}

// Modal quick-edit heures + note pour une journée précise
function DispoCellDialog({ editingCell, setEditingCell, items, updateDay }) {
  const date = editingCell?.date;
  const v = date ? items[date] || {} : {};
  const [local, setLocal] = useState(v);
  useEffect(() => { setLocal(date ? items[date] || {} : {}); }, [date, items]);
  if (!date) return null;
  const dateObj = parseIso(date);
  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && setEditingCell(null)}>
      <DialogContent data-testid="dispo-cell-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1A2B27] capitalize">
            {fmtFull(dateObj)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!local.available}
              onCheckedChange={(c) => setLocal({ ...local, available: c })}
              data-testid="dispo-cell-toggle"
            />
            <span className="text-sm font-medium text-[#1A2B27]">
              {local.available ? "Disponible" : "Non disponible"}
            </span>
          </div>
          {local.available && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-tiny">De</label>
                  <Input
                    type="time"
                    value={local.start || "08:30"}
                    onChange={(e) => setLocal({ ...local, start: e.target.value })}
                    data-testid="dispo-cell-start"
                    className="h-11 rounded-xl border-[#DDE7E4]"
                  />
                </div>
                <div>
                  <label className="label-tiny">À</label>
                  <Input
                    type="time"
                    value={local.end || "17:00"}
                    onChange={(e) => setLocal({ ...local, end: e.target.value })}
                    data-testid="dispo-cell-end"
                    className="h-11 rounded-xl border-[#DDE7E4]"
                  />
                </div>
              </div>
              <div>
                <label className="label-tiny">Note (optionnel)</label>
                <Textarea
                  value={local.comment || ""}
                  onChange={(e) => setLocal({ ...local, comment: e.target.value })}
                  data-testid="dispo-cell-comment"
                  className="rounded-xl border-[#DDE7E4] min-h-[70px]"
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setEditingCell(null)}
            data-testid="dispo-cell-cancel"
            className="rounded-full text-[#6b7f79]"
          >
            Annuler
          </Button>
          <Button
            onClick={() => {
              updateDay(date, {
                available: !!local.available,
                start: local.start || null,
                end: local.end || null,
                comment: local.comment || null,
              });
              setEditingCell(null);
              toast.success("Journée mise à jour — pense à Enregistrer");
            }}
            data-testid="dispo-cell-save"
            className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528]"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Dispos() {
  const { user } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [items, setItems] = useState({}); // date -> {available,start,end,comment}
  const [holidays, setHolidays] = useState({});
  const [busy, setBusy] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || "list"); // list | week | month
  const [weekOffset, setWeekOffset] = useState(0); // décalage semaine dans la période
  const [editingCell, setEditingCell] = useState(null); // {date} pour ouvrir modal quick-edit

  useEffect(() => { localStorage.setItem(VIEW_KEY, viewMode); }, [viewMode]);

  // Charger périodes + dispos étudiant
  useEffect(() => {
    api.get("/periods").then((r) => {
      setPeriods(r.data);
      const open = r.data.find((p) => p.open) || r.data[0];
      if (open) setPeriod(open);
    });
  }, []);

  useEffect(() => {
    if (!period) return;
    const start = period.start;
    const end = period.end;
    api.get("/availabilities", { params: { start, end, user_id: user.id } }).then((r) => {
      const map = {};
      r.data.forEach((a) => { map[a.date] = a; });
      setItems(map);
    });
    const year = parseIso(start).getFullYear();
    api.get(`/holidays/${year}`).then((r) => {
      const map = {};
      r.data.forEach((h) => { map[h.date] = h; });
      setHolidays(map);
    });
  }, [period, user.id]);

  const days = useMemo(() => {
    if (!period) return [];
    return rangeDays(parseIso(period.start), parseIso(period.end));
  }, [period]);

  const updateDay = (dateIso, patch) => {
    setItems((prev) => ({
      ...prev,
      [dateIso]: { ...(prev[dateIso] || { available: false }), date: dateIso, ...patch },
    }));
  };

  const save = async (forceCancel = false) => {
    setBusy(true);
    const payload = {
      force_cancel: forceCancel,
      items: Object.entries(items).map(([d, v]) => ({
        date: d,
        available: !!v.available,
        start: v.available ? (v.start || "08:30") : null,
        end: v.available ? (v.end || "17:00") : null,
        comment: v.comment || null,
      })),
    };
    try {
      const { data } = await api.post("/availabilities", payload);
      const n = (data.cancellations || []).length;
      if (n) {
        toast.success(`Disponibilités enregistrées. ${n} shift(s) annulé(s) — admin et chef notifiés.`);
      } else {
        toast.success("Disponibilités enregistrées");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Cas confirmation requise pour annulation
      if (e?.response?.status === 409 && detail && typeof detail === "object" && detail.code === "shift_cancellation_confirm") {
        const dates = (detail.shifts || []).map((s) => `${s.date} (${s.start}–${s.end})`).join(", ");
        const ok = window.confirm(
          `Cette modification va annuler ${detail.shifts.length} shift(s) publié(s) : ${dates}.\n\n` +
          `L'admin et le chef d'atelier seront prévenus avec la liste des remplaçants potentiels.\n\nConfirmer ?`
        );
        if (ok) {
          setBusy(false);
          return save(true);
        }
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  if (user.role !== "student") {
    return <DisposAdmin />;
  }

  return (
    <div className="space-y-6" data-testid="page-dispos-student">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Mes disponibilités</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
            Période {period?.label || "—"}
          </h1>
          <p className="text-[#6b7f79] mt-1">
            {period ? `Du ${fmtShort(parseIso(period.start))} au ${fmtShort(parseIso(period.end))}` : ""}
            {period && !period.open && <Badge variant="outline" className="ml-2 border-[#3C6750] text-[#3C6750]">Fermée</Badge>}
          </p>
        </div>
        <Button
          onClick={() => save()}
          disabled={busy || !period?.open}
          data-testid="dispos-save-button"
          className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] h-11 px-5 shadow-md hover:-translate-y-0.5 transition-all"
        >
          <Save className="w-4 h-4 mr-2" /> Enregistrer
        </Button>
      </div>

      {periods.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {periods.map((p) => (
            <Button
              key={p.id}
              variant={period?.id === p.id ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(p)}
              className={`rounded-full ${period?.id === p.id ? "bg-[#2C4C3B] hover:bg-[#1F3528]" : "border-[#DDE7E4] text-[#6b7f79]"}`}
              data-testid={`period-tab-${p.id}`}
            >
              {p.label}
            </Button>
          ))}
        </div>
      )}

      {/* Toggle vue Liste / Semaine / Mois */}
      {period && (
        <div className="inline-flex rounded-full bg-[#F1EDE3] p-1" data-testid="dispos-view-toggle">
          <ViewToggleBtn active={viewMode === "list"} onClick={() => setViewMode("list")} icon={<LayoutList className="w-4 h-4" />} label="Liste" testId="dispos-view-list" />
          <ViewToggleBtn active={viewMode === "week"} onClick={() => setViewMode("week")} icon={<CalendarDays className="w-4 h-4" />} label="Semaine" testId="dispos-view-week" />
          <ViewToggleBtn active={viewMode === "month"} onClick={() => setViewMode("month")} icon={<LayoutGrid className="w-4 h-4" />} label="Mois" testId="dispos-view-month" />
        </div>
      )}

      {!period && (
        <Card className="card-soft border-0 bg-[#F5FAF8]" data-testid="dispos-no-period">
          <CardContent className="p-6 text-center">
            <p className="font-display text-lg text-[#1A2B27] mb-1">Aucune période ouverte pour le moment</p>
            <p className="text-sm text-[#6b7f79]">
              Le chef d'atelier n'a pas encore ouvert de période de saisie de disponibilités.
              Merci de patienter — tu recevras un email dès qu'une nouvelle période sera disponible.
            </p>
          </CardContent>
        </Card>
      )}

      {period && viewMode === "list" && (
      <div className="space-y-2">
        {days.map((d) => {
          const isoD = iso(d);
          const hol = holidays[isoD];
          const isCustomClosure = hol && hol.source === "custom";
          const closed = d.getDay() === 0 || !!hol;
          const v = items[isoD] || {};
          return (
            <Card
              key={isoD}
              className={`card-soft border-0 transition-all ${closed ? "opacity-60" : ""} ${isCustomClosure ? "bg-[#DDECE8]" : ""}`}
              data-testid={`dispo-card-${isoD}`}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 sm:w-56 shrink-0">
                  <div className="text-center w-12">
                    <div className="text-[10px] uppercase tracking-wider text-[#94ADA7] font-semibold">
                      {JOURS_LONG[(d.getDay() + 6) % 7].slice(0, 3)}
                    </div>
                    <div className="font-display text-2xl text-[#1A2B27] leading-none">{d.getDate()}</div>
                  </div>
                  {closed ? (
                    <div className="flex flex-col gap-1">
                      <Badge variant="outline" className={`border-[#3C6750] text-[#3C6750] ${isCustomClosure ? "bg-white" : ""}`}>
                        {d.getDay() === 0 ? "Fermé" : hol?.source === "be" ? "Férié" : "Fermé"}
                      </Badge>
                      {hol?.name && <span className="text-[10px] text-[#6b7f79] italic">{hol.name}</span>}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!v.available}
                        onCheckedChange={(c) => updateDay(isoD, { available: c })}
                        disabled={!period?.open}
                        data-testid={`dispo-toggle-${isoD}`}
                      />
                      <span className="text-sm font-medium text-[#1A2B27]">
                        {v.available ? "Dispo" : "Non dispo"}
                      </span>
                    </div>
                  )}
                </div>

                {!closed && v.available && (
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <Input
                      type="time"
                      value={v.start || "08:30"}
                      onChange={(e) => updateDay(isoD, { start: e.target.value })}
                      disabled={!period?.open}
                      data-testid={`dispo-start-${isoD}`}
                      className="w-28 h-10 rounded-xl border-[#DDE7E4]"
                    />
                    <span className="text-[#94ADA7]">→</span>
                    <Input
                      type="time"
                      value={v.end || "17:00"}
                      onChange={(e) => updateDay(isoD, { end: e.target.value })}
                      disabled={!period?.open}
                      data-testid={`dispo-end-${isoD}`}
                      className="w-28 h-10 rounded-xl border-[#DDE7E4]"
                    />
                    <Input
                      placeholder="Note (optionnel)"
                      value={v.comment || ""}
                      onChange={(e) => updateDay(isoD, { comment: e.target.value })}
                      disabled={!period?.open}
                      data-testid={`dispo-comment-${isoD}`}
                      className="flex-1 min-w-40 h-10 rounded-xl border-[#DDE7E4]"
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {period && viewMode === "week" && (
        <DisposWeekGrid
          days={days}
          items={items}
          holidays={holidays}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
          onToggle={(d, avail) => updateDay(d, { available: avail })}
          onEditCell={(d) => setEditingCell({ date: d })}
          disabled={!period?.open}
        />
      )}

      {period && viewMode === "month" && (
        <DisposMonthGrid
          days={days}
          items={items}
          holidays={holidays}
          onToggle={(d, avail) => updateDay(d, { available: avail })}
          onEditCell={(d) => setEditingCell({ date: d })}
          disabled={!period?.open}
        />
      )}

      {/* Dialog édition rapide horaires/note pour une journée */}
      <DispoCellDialog
        editingCell={editingCell}
        setEditingCell={setEditingCell}
        items={items}
        updateDay={updateDay}
      />
    </div>
  );
}

// ----- ADMIN/CHEF -----
function fmtReminderDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mn = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm} à ${hh}h${mn}`;
  } catch { return iso; }
}

function DisposAdmin() {
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [grid, setGrid] = useState(null);
  const [reminders, setReminders] = useState({});
  const [remindingId, setRemindingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  // { type: "one" | "all", student?: {id,name}, students?: [...] }
  const [confirm, setConfirm] = useState(null);

  const missing = grid?.response_status?.filter((s) => !s.responded) || [];

  const loadReminders = async (pid) => {
    try {
      const { data } = await api.get("/availabilities/reminders", { params: { period_id: pid } });
      setReminders(data || {});
    } catch (e) { console.warn("reminders load failed:", e); }
  };

  const sendReminderOne = async (s) => {
    setRemindingId(s.id);
    try {
      const { data } = await api.post("/availabilities/remind-one", { student_id: s.id });
      toast.success(`Rappel envoyé à ${data.to} ✓`);
      if (period) loadReminders(period.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRemindingId(null); setConfirm(null); }
  };

  const sendReminderAll = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/availabilities/remind-missing", {
        student_ids: missing.map((m) => m.id),
      });
      if (data.failed?.length) {
        toast.warning(`${data.sent_count} envoi(s) — ${data.failed.length} échec(s)`);
      } else {
        toast.success(`${data.sent_count} rappel${data.sent_count > 1 ? "s" : ""} envoyé${data.sent_count > 1 ? "s" : ""} ✓`);
      }
      if (period) loadReminders(period.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); setConfirm(null); }
  };

  useEffect(() => {
    api.get("/periods").then((r) => {
      setPeriods(r.data);
      const open = r.data.find((p) => p.open) || r.data[0];
      if (open) setPeriod(open);
    });
  }, []);

  useEffect(() => {
    if (!period) return;
    api.get("/availabilities/grid", { params: { start: period.start, end: period.end } })
      .then((r) => setGrid(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    loadReminders(period.id);
  }, [period]);

  const openNewPeriod = async (mode = "next") => {
    const today = new Date();
    const target = mode === "current"
      ? new Date(today.getFullYear(), today.getMonth(), 1)
      : new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const last = new Date(target.getFullYear(), target.getMonth() + 1, 0);
    try {
      const { data } = await api.post("/periods", {
        label: `${String(target.getMonth() + 1).padStart(2, "0")}/${target.getFullYear()}`,
        start: iso(target),
        end: iso(last),
        open: true,
      });
      setPeriods([data, ...periods]);
      setPeriod(data);
      toast.success("Période ouverte");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const togglePeriodOpen = async () => {
    if (!period) return;
    const willClose = period.open;
    if (willClose && !window.confirm(`Fermer la période « ${period.label} » ? Les étudiants ne pourront plus modifier leurs disponibilités.`)) return;
    try {
      const { data } = await api.patch(`/periods/${period.id}`, {
        label: period.label, start: period.start, end: period.end, open: !period.open,
      });
      setPeriods(periods.map((p) => (p.id === data.id ? data : p)));
      setPeriod(data);
      toast.success(data.open ? "Période ouverte" : "Période fermée");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="page-dispos-admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-tiny">Disponibilités équipe</p>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
            {period?.label || "—"}
          </h1>
          <p className="text-[#6b7f79] mt-1">
            {period && `Du ${fmtShort(parseIso(period.start))} au ${fmtShort(parseIso(period.end))}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {period && (
            <Button
              onClick={togglePeriodOpen}
              variant="outline"
              data-testid="toggle-period-open-button"
              className={`rounded-full h-11 ${period.open ? "border-[#C62828] text-[#C62828] hover:bg-[#FFEBEE]" : "border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E4EBE1]"}`}
            >
              {period.open ? <><Ban className="w-4 h-4 mr-2" /> Fermer cette période</> : <><Check className="w-4 h-4 mr-2" /> Rouvrir cette période</>}
            </Button>
          )}
          <Button
            onClick={() => openNewPeriod("current")}
            variant="outline"
            data-testid="open-current-period-button"
            className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E4EBE1] h-11"
          >
            <CalendarRange className="w-4 h-4 mr-2" /> Mois en cours
          </Button>
          <Button
            onClick={() => openNewPeriod("next")}
            data-testid="open-new-period-button"
            className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3528] h-11 shadow-md"
          >
            <CalendarRange className="w-4 h-4 mr-2" /> Ouvrir mois suivant
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {periods.map((p) => (
          <Button
            key={p.id}
            variant={period?.id === p.id ? "default" : "outline"}
            size="sm"
            onClick={() => setPeriod(p)}
            className={`rounded-full ${period?.id === p.id ? "bg-[#2C4C3B] hover:bg-[#1F3528]" : "border-[#DDE7E4] text-[#6b7f79]"}`}
            data-testid={`admin-period-tab-${p.id}`}
          >
            {p.label} {p.open ? <Check className="w-3 h-3 ml-1" /> : <X className="w-3 h-3 ml-1" />}
          </Button>
        ))}
      </div>

      {grid && (
        <>
          <Card className="card-soft border-0" data-testid="admin-response-status">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <h2 className="font-display text-lg text-[#1A2B27] flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#2C4C3B]" /> État des réponses
                </h2>
                {missing.length > 0 && (
                  <Button
                    size="sm"
                    onClick={() => setConfirm({ type: "all", students: missing })}
                    disabled={bulkBusy}
                    data-testid="remind-all-button"
                    className="rounded-full bg-[#C62828] hover:bg-[#A52121] text-white shadow-sm h-8 px-3"
                  >
                    <BellRing className="w-3.5 h-3.5 mr-1.5" />
                    Tout relancer ({missing.length})
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <TooltipProvider>
                {grid.response_status.map((s) =>
                  s.responded ? (
                    <Badge
                      key={s.id}
                      data-testid={`status-${s.id}-responded`}
                      className="rounded-full px-3 py-1 bg-[#1B5E20] hover:bg-[#1B5E20] text-white border-0 shadow-sm"
                    >
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                      {s.name} <span className="opacity-80 ml-1">({s.count})</span>
                    </Badge>
                  ) : (
                    <Tooltip key={s.id} delayDuration={150}>
                      <TooltipTrigger asChild>
                        <div
                          data-testid={`status-${s.id}-missing`}
                          className="inline-flex items-center gap-1 rounded-full bg-[#C62828] text-white pl-3 pr-1 py-1 shadow-sm cursor-default"
                        >
                          <span className="text-xs font-semibold">{s.name} — pas encore</span>
                          <button
                            type="button"
                            onClick={() => setConfirm({ type: "one", student: s })}
                            disabled={remindingId === s.id}
                            data-testid={`remind-${s.id}`}
                            title={`Envoyer un rappel à ${s.name}`}
                            className="ml-1 flex items-center justify-center w-7 h-7 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/40 transition-colors disabled:opacity-50"
                          >
                            <BellRing className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="bottom"
                        className="bg-[#1A2B27] text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-[260px]"
                        data-testid={`reminder-history-${s.id}`}
                      >
                        {(reminders[s.id]?.length || 0) === 0 ? (
                          <span className="italic opacity-80">Aucune relance envoyée pour cette période</span>
                        ) : (
                          <div className="space-y-1">
                            <div className="font-semibold mb-1 text-[#B8DBD4]">Historique des relances</div>
                            {(reminders[s.id] || []).slice(0, 5).map((r, i) => (
                              <div key={`${r.sent_at}-${i}`}>
                                {r.by || "—"} <span className="opacity-70">le {fmtReminderDate(r.sent_at)}</span>
                              </div>
                            ))}
                            {(reminders[s.id] || []).length > 5 && (
                              <div className="opacity-70 italic">
                                +{reminders[s.id].length - 5} {reminders[s.id].length - 5 > 1 ? "autres" : "autre"}…
                              </div>
                            )}
                          </div>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )
                )}
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>

          <Card className="card-soft border-0 bg-[#F5FAF8]" data-testid="admin-planning-hint">
            <CardContent className="p-4 flex items-start gap-3">
              <CalendarRange className="w-5 h-5 text-[#2C4C3B] shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-[#1A2B27]">Pour voir les dispos jour par jour</div>
                <div className="text-[#6b7f79] mt-0.5">
                  Utilise le toggle <b>« Voir les dispos »</b> sur la page{" "}
                  <Link to="/planning" className="text-[#2C4C3B] font-semibold hover:underline">Planning</Link>
                  {" "}— chaque cellule vide sera teintée selon la dispo de l&apos;étudiant.
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="remind-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A2B27]">
              {confirm?.type === "all" ? "Tout relancer ?" : "Envoyer une relance ?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#3D504B]">
              {confirm?.type === "all" ? (
                <>
                  Êtes-vous sûr de vouloir relancer tous les membres n&apos;ayant pas encore répondu ?<br />
                  <span className="block mt-2 text-xs">
                    {confirm.students?.map((s) => s.name).join(", ")} ({confirm.students?.length} personne{confirm.students?.length > 1 ? "s" : ""})
                  </span>
                </>
              ) : (
                <>Êtes-vous sûr de vouloir relancer <b>{confirm?.student?.name}</b> ?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remind-confirm-cancel" className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="remind-confirm-action"
              disabled={bulkBusy || !!remindingId}
              onClick={(e) => {
                e.preventDefault();
                if (confirm?.type === "all") sendReminderAll();
                else if (confirm?.student) sendReminderOne(confirm.student);
              }}
              className="rounded-full bg-[#C62828] hover:bg-[#A52121]"
            >
              <BellRing className="w-4 h-4 mr-1.5" /> Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
